// DOM-free deep-link helpers for PATH-based invite/subscribe/profile URLs.
//
// The portal SPA is hash-routed, but public invite links are PLAIN PATHS so they are
// human-meaningful and printable:
//   /id/<alias>                      -> public profile preview
//   /id/<alias>/subscribe            -> subscribe (generic, needs a code or ?token=)
//   /id/<alias>/invite/<code>        -> coded-invite redeem (owner-scoped code)
//
// Caddy serves index.html for /id/* (SPA fallback), so on boot we translate a matching
// pathname into the equivalent hash route BEFORE the router starts. These helpers are
// pure (no DOM / no window) so they are unit-testable.

export interface InvitePath {
  alias: string;
  code?: string;
  /** kind of deep link matched */
  kind: 'profile' | 'subscribe' | 'invite';
}

// parseInvitePath maps a pathname to a structured invite deep-link, or null when the
// path is not an /id/... link. Trailing slashes are tolerated. Segments are returned
// decoded so callers get real alias/code values.
export function parseInvitePath(pathname: string): InvitePath | null {
  if (!pathname) return null;
  // Strip a leading/trailing slash then split. An empty string -> [].
  const parts = pathname.replace(/^\/+/, '').replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts.length < 2 || parts[0] !== 'id') return null;

  const dec = (s: string): string => {
    try { return decodeURIComponent(s); } catch { return s; }
  };
  const alias = dec(parts[1]);
  if (!alias) return null;

  // /id/<alias>
  if (parts.length === 2) return { alias, kind: 'profile' };

  // /id/<alias>/subscribe
  if (parts.length === 3 && parts[2] === 'subscribe') return { alias, kind: 'subscribe' };

  // /id/<alias>/invite/<code>
  if (parts.length === 4 && parts[2] === 'invite') {
    const code = dec(parts[3]);
    if (!code) return null;
    return { alias, code, kind: 'invite' };
  }

  return null;
}

// hashForInvite converts a parsed deep-link into the equivalent in-app hash route.
// Keeps any query string (e.g. ?token= fallback) so both flows compose.
export function hashForInvite(p: InvitePath, search = ''): string {
  const qs = search && search !== '?' ? (search.startsWith('?') ? search : '?' + search) : '';
  switch (p.kind) {
    case 'invite':
      return `#/id/${encodeURIComponent(p.alias)}/invite/${encodeURIComponent(p.code || '')}${qs}`;
    case 'subscribe':
      return `#/id/${encodeURIComponent(p.alias)}/subscribe${qs}`;
    case 'profile':
    default:
      return `#/id/${encodeURIComponent(p.alias)}${qs}`;
  }
}
